import { Dispatch, SetStateAction, useMemo } from 'react'
import styles from './DateInput.module.css'
import { ProjectAttributes } from '../../../../interfaces/project/ProjectAttributes'
import { useInteractionContext } from '../../../../context/InteractionContext'
import { useMemorizedEditing } from '../../customHooks/useMemorizedEditing'

interface MonthDateInputProps {
  values: ProjectAttributes
  initialValues: ProjectAttributes
  label: string
  setValues: Dispatch<SetStateAction<ProjectAttributes>>
  setChangedValue: Dispatch<
    SetStateAction<{ [key: string]: string | number | null }>
  >
  attributeName: string
  disabled: boolean
}

// Function to check if a label should be treated as multiline
const isMultilineLabel = (label: string) => {
  return label?.length > 20
}

// Function to handle long labels, formatting them for better display
const formatLongLabel = (label: string) => {
  // No more replacement of periods with line breaks
  // Let CSS handle the wrapping naturally
  return label || ''
}

export const MonthDateInput = ({
  values,
  initialValues,
  setValues,
  setChangedValue,
  label,
  attributeName,
  disabled,
}: MonthDateInputProps) => {
  const { refreshLoading } = useInteractionContext()

  const { setInputBlurEvent, setKeyTriggerEvent, handleChangedDate } =
    useMemorizedEditing({
      values,
      initialValues,
      setValues,
      setChangedValue,
      attributeName,
      regExp: /^(19[4-9]\d|20\d{2})-(0[1-9]|1[0-2])$/,
    })

  const monthDateInput = () => (
    <input
      lang="sv-Sv"
      data-testid={attributeName}
      value={(values[attributeName as keyof ProjectAttributes] as string) ?? ''}
      className={styles.dateInput}
      type={'month'}
      min={'1940-01'}
      max={'9999-12'}
      disabled={disabled}
      tabIndex={0}
      onChange={(e) => handleChangedDate(e)}
      onKeyDown={(e) => setKeyTriggerEvent(e)}
      onBlur={(e) => setInputBlurEvent(e)}
    />
  )

  const disabledMonthDateInput = () => (
    <input
      lang="sv-Sv"
      data-testid={attributeName}
      value={(values[attributeName as keyof ProjectAttributes] as string) ?? ''}
      className={styles.dateInput}
      type={'month'}
      disabled
    />
  )

  // Format the label for display
  const formattedLabel = useMemo(() => formatLongLabel(label), [label])
  const isMultiline = useMemo(() => isMultilineLabel(label), [label])

  return (
    <div className={styles.dateInputContainer}>
      <label
        className={`${styles.dateInputLabel} ${
          isMultiline ? styles.multilineLabel : ''
        }`}
      >
        {formattedLabel}
      </label>
      <span className={styles.dateInputHolder}>
        {refreshLoading ? disabledMonthDateInput() : monthDateInput()}
      </span>
    </div>
  )
}
